import { useCallback } from 'react';
import { useImmerAtom } from 'jotai-immer';
import { useSetAtom } from 'jotai';
import { systemStateAtom, SystemState } from './system_atoms';
import { dataMonitorPageStateAtom } from '../datamonitor_list_atoms';
import datamonitorRepo, { SystemStatItem, SystemStatType } from '../../data/datamonitorRepo';

type SystemSection = 'system' | 'integrity';

type UseSystemDataReturn = SystemState & {
    load: () => Promise<void>;
    toggle: (section: SystemSection, id: string) => void;
};

// Merge fresh health rows into the current rows, preserving expand/events state (legacy loadRefresh).
const mergeRows = (current: SystemStatItem[], incoming: SystemStatItem[]): void => {
    incoming.forEach(next => {
        const row = current.find(r => r.MonitorType === next.MonitorType);
        if (!row) {
            current.push(next);
            return;
        }
        row.Status = next.Status;
        row.Variance = next.Variance;
        row.Changed = next.Changed;
        row.LastChecked = next.LastChecked;
        row.StartTime = next.StartTime;
        row.ElapsedTxt = next.ElapsedTxt;
    });
};

export const useSystemData = (): UseSystemDataReturn => {
    const [state, setState] = useImmerAtom(systemStateAtom);
    const setPageState = useSetAtom(dataMonitorPageStateAtom);

    const load = useCallback(async (): Promise<void> => {
        setState(draft => {
            draft.waiting = draft.init;
            draft.error = null;
        });
        try {
            const health = await datamonitorRepo.getSystemHealth();
            setState(draft => {
                mergeRows(draft.system, health.system);
                mergeRows(draft.integrity, health.integrity);
                draft.init = false;
                draft.waiting = false;
            });
            // keep the WAC circuit-breaker control in sync with each System refresh
            setPageState(prev => ({ ...prev, wacOrderingStatus: health.wacOrderingStatus }));
        } catch {
            setState(draft => {
                draft.init = false;
                draft.waiting = false;
                draft.error = 'Unable to load system health.';
            });
        }
    }, [setState, setPageState]);

    const fetchEvents = useCallback(
        async (section: SystemSection, id: string): Promise<void> => {
            const type: SystemStatType = section === 'system' ? 'SYSTEM' : 'INTEGRITY';
            try {
                const events = await datamonitorRepo.getStatEvents(type, id);
                setState(draft => {
                    const row = (section === 'system' ? draft.system : draft.integrity).find(r => r.Id === id);
                    if (row) {
                        row.events = events;
                        row.eventsLoaded = true;
                    }
                });
            } catch {
                setState(draft => {
                    const row = (section === 'system' ? draft.system : draft.integrity).find(r => r.Id === id);
                    if (row) {
                        row.expand = false;
                    }
                });
            }
        },
        [setState],
    );

    const toggle = useCallback(
        (section: SystemSection, id: string): void => {
            const rows = section === 'system' ? state.system : state.integrity;
            const row = rows.find(r => r.Id === id);
            if (!row) return;

            const willExpand = !row.expand;
            const needFetch = willExpand && !row.eventsLoaded;

            setState(draft => {
                const target = (section === 'system' ? draft.system : draft.integrity).find(r => r.Id === id);
                if (!target) return;
                target.expand = willExpand;
                if (!willExpand) {
                    // Legacy clears events on collapse and re-fetches on next expand.
                    target.events = [];
                    target.eventsLoaded = false;
                }
            });

            if (needFetch) fetchEvents(section, id);
        },
        [state, setState, fetchEvents],
    );

    return { ...state, load, toggle };
};
